import { useCallback, useMemo } from "react";
import { useSocialCoachSession } from "./useSocialCoachSession";
import { useLocalStorage } from "./useLocalStorage";
import { STORAGE_KEYS } from "./storage";
import { ImpersonationServices } from "@/services/bedrock/ImpersonationServices";
import { ImpersonatableUsers } from "@/services/bedrock/dtos";
import { clearUserStorage } from "./storage";

export const useImpersonation = () => {
  const { session } = useSocialCoachSession();
  const currentPlayerId = session?.playerId;

  const [storedRecentUsers, setRecentUsers] = useLocalStorage<ImpersonatableUsers[]>(
    STORAGE_KEYS.RECENT_IMPERSONATIONS,
    []
  );

  // Filter out the currently active user/session so they don't see themselves in the Recents list
  const recentUsers = useMemo(() => {
    if (!session?.userGuid) return storedRecentUsers;
    return storedRecentUsers.filter(u => u.guid !== session.userGuid);
  }, [storedRecentUsers, session?.userGuid]);

  /**
   * Securely triggers impersonation, cleans up session-scoped local state (like onboarding bypass flags),
   * and redirects to home root to avoid loading cached resource states.
   *
   * If a full ImpersonatableUsers object is provided instead of a raw guid, they will automatically
   * be registered into the Admin's "Recently Impersonated" local history list.
   */
  const impersonate = useCallback(
    async (user: string | ImpersonatableUsers) => {
      const userGuid = typeof user === "string" ? user : user.guid;

      try {
        await ImpersonationServices.impersonateUser(userGuid);

        // 1. If we have the full user object, sync it to the Recents list
        if (typeof user !== "string") {
          const filtered = storedRecentUsers.filter(u => u.guid !== user.guid);
          setRecentUsers([user, ...filtered].slice(0, 5));
        }

        // 2. Clear session-specific state (like onboarding skip) of the prior user
        // isExplicitLogout defaults to false so we preserve 'recent_impersonations'!
        clearUserStorage(currentPlayerId);

        // 3. Redirect to root so we don't try to load user A's specific URLs as user B
        window.location.href = "/";
      } catch (e) {
        console.error("Impersonation failed:", e);
        throw e;
      }
    },
    [currentPlayerId, storedRecentUsers, setRecentUsers]
  );

  /**
   * Securely reverts impersonation, cleans up local state, and redirects to home root.
   */
  const revert = useCallback(async () => {
    try {
      await ImpersonationServices.revertImpersonation();

      // Clear session-specific state but preserve history
      clearUserStorage(currentPlayerId);

      window.location.href = "/";
    } catch (e) {
      console.error("Failed to revert impersonation:", e);
      throw e;
    }
  }, [currentPlayerId]);

  return { impersonate, revert, recentUsers };
};
